import { c as _c } from "react/compiler-runtime";
import ProductAttributeTileListComponent, { ProductAttributeTileData } from '../product-attribute-tile-list/product-attribute-tile-list.component';
import type { ProductInfoProduct, ProductInfoProductSibling } from '../product-info/ProductInfoProductTypes';
import { ReactElement, memo } from 'react';
import { SiblingsAttributes, extractUniqueAttributes } from '@spa/components/product-attribute-list/extractProductAttributeListData';
import { AttributeValue } from '@spa/components/product-specifications/AttributeTypes';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import formatAttributeValue from '@spa/lib/formatAttributeValue';
import styles from './product-attribute-list.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
export interface Props {
  readonly product: ProductInfoProduct;
}
export enum ProductAttributeType {
  CAPACITY_VALUE = 'capacityValue',
  CLOTHING_SIZE = 'clothingSize',
  INK_COLOR = 'inkColor',
  MANUFACTURER_COLOR = 'manufacturerColor',
  SIZE_RANGE = 'sizeRange',
  STORAGE_CAPACITY = 'storageCapacity',
  STORAGE_UNIT = 'storageUnit',
}
export interface ProductAttributeData {
  readonly isPrimary?: boolean;
  readonly mapValue?: (value: string) => string;
  readonly selectedName: string | null;
  readonly selectedId: string | null;
  readonly type: ProductAttributeType;
  readonly uniqueElements?: Map<string, ReactElement>;
  readonly uniqueNames: Array<string>;
}
const identityAttributeValue = (value: string): string => value;
function ProductAttributeListComponent(t0) {
  const $ = _c(15);
  const {
    product: currentProduct
  } = t0;
  const {
    capacityUnitCode: currentCapacityUnitCode,
    capacityValue: currentCapacityValue,
    clothingSize: currentClothingSize,
    inkColor: currentInkColor,
    manufacturerColor: currentManufacturerColor,
    manufacturerColorNumber: currentManufacturerColorNumber,
    siblings,
    sizeRange: currentSizeRange,
    storageCapacity: currentStorageCapacity,
    storageUnit: currentStorageUnit
  } = currentProduct;
  const {
    formatMessage
  } = useNamespacedIntl("product-attribute-list");
  const {
    formatMessage: capacityUnitsFormatMessage
  } = useNamespacedIntl("capacity-units");
  let t1;
  if ($[0] !== capacityUnitsFormatMessage || $[1] !== currentCapacityUnitCode || $[2] !== currentCapacityValue || $[3] !== currentClothingSize || $[4] !== currentInkColor || $[5] !== currentManufacturerColor || $[6] !== currentManufacturerColorNumber || $[7] !== currentProduct || $[8] !== currentSizeRange || $[9] !== currentStorageCapacity || $[10] !== currentStorageUnit || $[11] !== formatMessage || $[12] !== siblings) {
    const {
      manufacturerColorUniqueElements,
      manufacturerColorUniqueNames,
      capacityValueUniqueNames,
      capacityValueUnitCodeMap,
      capacityValueInnerPackCountMap,
      clothingSizeUniqueNames,
      inkColorUniqueNames,
      sizeRangeUniqueNames,
      storageUnitUniqueNames,
      storageCapacityUniqueNames
    } = extractUniqueAttributes(siblings, currentProduct);
    const clothingSizesOrder = ["XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL", "4XL"];
    const storageUnitsOrder = ["MB (Megabyte)", "GB (Gigabyte)", "TB (Terabyte)", "PB (Petabyte)", "Minuten"];
    const productAttributeListData = [{
      isPrimary: true,
      selectedId: currentManufacturerColor,
      selectedName: currentManufacturerColorNumber ? `${currentManufacturerColor} - ${currentManufacturerColorNumber}` : currentManufacturerColor,
      type: ProductAttributeType.MANUFACTURER_COLOR,
      uniqueElements: manufacturerColorUniqueElements,
      uniqueNames: manufacturerColorUniqueNames
    }, {
      isPrimary: true,
      mapValue: capacityValue => {
        const capacityUnit = capacityUnitsFormatMessage(capacityValueUnitCodeMap.get(capacityValue) ?? currentCapacityUnitCode);
        const siblingInnerPackCount = capacityValueInnerPackCountMap.get(capacityValue);
        return `${capacityValue} ${capacityUnit}${siblingInnerPackCount ? ` / ${formatAttributeValue(siblingInnerPackCount)}` : ""}`;
      },
      selectedId: currentCapacityValue,
      selectedName: currentCapacityValue,
      type: ProductAttributeType.CAPACITY_VALUE,
      uniqueNames: [...capacityValueUniqueNames].sort(_temp)
    }, {
      mapValue: _temp2,
      selectedId: currentClothingSize,
      selectedName: currentClothingSize,
      type: ProductAttributeType.CLOTHING_SIZE,
      uniqueNames: [...clothingSizeUniqueNames].sort((a_0, b_0) => clothingSizesOrder.indexOf(a_0.toUpperCase()) - clothingSizesOrder.indexOf(b_0.toUpperCase()))
    }, {
      isPrimary: true,
      selectedId: currentInkColor,
      selectedName: currentInkColor,
      type: ProductAttributeType.INK_COLOR,
      uniqueNames: inkColorUniqueNames
    }, {
      selectedId: currentSizeRange,
      selectedName: currentSizeRange,
      type: ProductAttributeType.SIZE_RANGE,
      uniqueNames: sizeRangeUniqueNames
    }, {
      isPrimary: true,
      selectedId: currentStorageUnit,
      selectedName: currentStorageUnit,
      type: ProductAttributeType.STORAGE_UNIT,
      uniqueNames: [...storageUnitUniqueNames].sort((a_1, b_1) => storageUnitsOrder.indexOf(a_1) - storageUnitsOrder.indexOf(b_1))
    }, {
      selectedId: currentStorageCapacity,
      selectedName: currentStorageCapacity,
      type: ProductAttributeType.STORAGE_CAPACITY,
      uniqueNames: [...storageCapacityUniqueNames].sort(_temp3)
    }];
    const findTargetProduct = (siblings_0, currentAttributeType, uniqueName) => siblings_0.find(sibling => sibling[currentAttributeType] === uniqueName && productAttributeListData.every(t2 => {
      const {
        selectedName: otherAttributeSelectedName,
        type: otherAttributeType
      } = t2;
      return otherAttributeType === currentAttributeType || otherAttributeSelectedName === sibling[otherAttributeType];
    }));
    const resolveTargetProduct = (currentAttributeType_0, uniqueName_0, isPrimary) => {
      const exactMatch = findTargetProduct(siblings, currentAttributeType_0, uniqueName_0);
      const inStockSibling = siblings.find(sibling_0 => sibling_0[currentAttributeType_0] === uniqueName_0 && sibling_0.stockLevel);
      const anySibling = siblings.find(sibling_1 => sibling_1[currentAttributeType_0] === uniqueName_0);
      const shouldPreferInStock = isPrimary && !exactMatch?.stockLevel;
      return shouldPreferInStock ? inStockSibling ?? exactMatch ?? anySibling : exactMatch;
    };
    let t3;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = classNames({
        [styles["product-attribute-list"]]: true
      });
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t1 = <div className={t3}>{productAttributeListData.map(t4 => {
        const {
          isPrimary: isPrimary_0,
          mapValue,
          selectedName: currentAttributeSelectedName,
          selectedId: currentAttributeSelectedId,
          type: currentAttributeType_1,
          uniqueNames,
          uniqueElements
        } = t4;
        const resolvedMapValue = mapValue ?? identityAttributeValue;
        return currentAttributeSelectedId && <div className={styles["product-attribute-list__attribute-wrapper"]} key={currentAttributeType_1}><div className="text-lg">{`${formatMessage(currentAttributeType_1)}: `}<span className="bold">{resolvedMapValue(currentAttributeSelectedName ?? currentAttributeSelectedId)}</span></div>{uniqueNames.length > 1 && <ProductAttributeTileListComponent attributeTiles={uniqueNames.map(uniqueName_1 => {
            let targetProduct;
            if (uniqueName_1 === currentAttributeSelectedName) {
              targetProduct = currentProduct;
            } else {
              targetProduct = resolveTargetProduct(currentAttributeType_1, uniqueName_1, isPrimary_0);
            }
            return {
              element: uniqueElements?.get(uniqueName_1),
              isAvailable: !!targetProduct?.stockLevel,
              name: resolvedMapValue(uniqueName_1),
              path: targetProduct?.path
            };
          })} isScrollable={currentAttributeType_1 === ProductAttributeType.MANUFACTURER_COLOR} selectedAttributeTileName={resolvedMapValue(currentAttributeSelectedId)} />}</div>;
      })}</div>;
    $[0] = capacityUnitsFormatMessage;
    $[1] = currentCapacityUnitCode;
    $[2] = currentCapacityValue;
    $[3] = currentClothingSize;
    $[4] = currentInkColor;
    $[5] = currentManufacturerColor;
    $[6] = currentManufacturerColorNumber;
    $[7] = currentProduct;
    $[8] = currentSizeRange;
    $[9] = currentStorageCapacity;
    $[10] = currentStorageUnit;
    $[11] = formatMessage;
    $[12] = siblings;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  return t1;
}
function _temp3(a_2, b_2) {
  return Number(a_2) - Number(b_2);
}
function _temp2(clothingSize) {
  return clothingSize.toUpperCase();
}
function _temp(a, b) {
  return Number(a) - Number(b);
}
export default memo(ProductAttributeListComponent);